import { useState, useCallback, useMemo } from 'react';
import {
  parseAnswer,
  stringifyAnswer,
  validateConnection,
  connectionsEqual,
} from './mappingUtils';

/**
 * Custom hook for managing mapping answer state
 * Handles connections, selection, history, and validation
 */
const useMappingAnswer = (initialValue, config, onChange) => {
  // Parse initial connections from answer string
  const initialConnections = useMemo(() => {
    return parseAnswer(initialValue).connections;
  }, [initialValue]);

  // State
  const [connections, setConnections] = useState(initialConnections);
  const [selectedLeft, setSelectedLeft] = useState(null);
  const [history, setHistory] = useState([]);

  // Sync with external value changes
  const syncWithExternalValue = useCallback(
    (newValue) => {
      const parsed = parseAnswer(newValue);
      if (JSON.stringify(parsed.connections) !== JSON.stringify(connections)) {
        setConnections(parsed.connections);
        setHistory([]);
      }
    },
    [connections]
  );

  // Save state to history before changes
  const saveToHistory = useCallback(() => {
    setHistory((prev) => [...prev, [...connections]]);
  }, [connections]);

  // Notify parent of changes
  const notifyChange = useCallback(
    (newConnections) => {
      if (onChange) {
        onChange(stringifyAnswer(newConnections));
      }
    },
    [onChange]
  );

  // Add a new connection
  const addConnection = useCallback(
    (fromId, toId) => {
      const validation = validateConnection(fromId, toId, connections, config);

      if (validation.valid) {
        saveToHistory();
        const newConnections = [...connections, { from: fromId, to: toId }];
        setConnections(newConnections);
        notifyChange(newConnections);
        setSelectedLeft(null);
        return { success: true };
      }

      // Handle replacing existing connection when multiple from left not allowed
      if (validation.replaceExisting && validation.existingConnection) {
        saveToHistory();
        const newConnections = connections.filter(
          (c) => !connectionsEqual(c, validation.existingConnection)
        );
        newConnections.push({ from: fromId, to: toId });
        setConnections(newConnections);
        notifyChange(newConnections);
        setSelectedLeft(null);
        return { success: true, replaced: true };
      }

      return { success: false, reason: validation.reason };
    },
    [connections, config, saveToHistory, notifyChange]
  );

  // Remove a connection
  const removeConnection = useCallback(
    (fromId, toId) => {
      saveToHistory();
      const newConnections = connections.filter(
        (c) => !(c.from === fromId && c.to === toId)
      );
      setConnections(newConnections);
      notifyChange(newConnections);
    },
    [connections, saveToHistory, notifyChange]
  );

  // Remove all connections from a specific left item
  const removeConnectionsFromLeft = useCallback(
    (fromId) => {
      saveToHistory();
      const newConnections = connections.filter((c) => c.from !== fromId);
      setConnections(newConnections);
      notifyChange(newConnections);
    },
    [connections, saveToHistory, notifyChange]
  );

  // Undo last action
  const undo = useCallback(() => {
    if (history.length === 0) return false;

    const previousState = history[history.length - 1];
    setHistory((prev) => prev.slice(0, -1));
    setConnections(previousState);
    notifyChange(previousState);
    setSelectedLeft(null);
    return true;
  }, [history, notifyChange]);

  // Clear all connections
  const clear = useCallback(() => {
    if (connections.length === 0) return false;

    saveToHistory();
    setConnections([]);
    notifyChange([]);
    setSelectedLeft(null);
    return true;
  }, [connections, saveToHistory, notifyChange]);

  // Check if a left item is connected
  const isLeftConnected = useCallback(
    (leftId) => {
      return connections.some((c) => c.from === leftId);
    },
    [connections]
  );

  // Check if a right item is connected
  const isRightConnected = useCallback(
    (rightId) => {
      return connections.some((c) => c.to === rightId);
    },
    [connections]
  );

  // Get all connections for a specific left item
  const getConnectionsFromLeft = useCallback(
    (leftId) => {
      return connections.filter((c) => c.from === leftId);
    },
    [connections]
  );

  // Get all connections to a specific right item
  const getConnectionsToRight = useCallback(
    (rightId) => {
      return connections.filter((c) => c.to === rightId);
    },
    [connections]
  );

  // Handle left item click
  const handleLeftClick = useCallback(
    (itemId) => {
      if (selectedLeft === itemId) {
        // Deselect if clicking same item
        setSelectedLeft(null);
      } else {
        setSelectedLeft(itemId);
      }
    },
    [selectedLeft]
  );

  // Handle right item click
  const handleRightClick = useCallback(
    (itemId) => {
      if (selectedLeft) {
        addConnection(selectedLeft, itemId);
      }
    },
    [selectedLeft, addConnection]
  );

  // Handle connection click (to remove)
  const handleConnectionClick = useCallback(
    (fromId, toId) => {
      removeConnection(fromId, toId);
    },
    [removeConnection]
  );

  return {
    // State
    connections,
    selectedLeft,
    history,
    canUndo: history.length > 0,
    canClear: connections.length > 0,

    // Setters
    setSelectedLeft,
    setConnections,

    // Actions
    addConnection,
    removeConnection,
    removeConnectionsFromLeft,
    undo,
    clear,

    // Queries
    isLeftConnected,
    isRightConnected,
    getConnectionsFromLeft,
    getConnectionsToRight,

    // Event handlers
    handleLeftClick,
    handleRightClick,
    handleConnectionClick,

    // Sync
    syncWithExternalValue,
  };
};

export default useMappingAnswer;
